import type {
  AssessmentContent,
  FacilitatorReportFilters,
  FacilitatorReportDto,
  InProgressAssessmentDto,
  SaveInProgressBody,
  SubmittedAssessmentDto,
} from './types';

/**
 * Thin typed fetch wrapper over the AITA API (plan §6.2). One code path for both
 * real and mock modes: the bearer token is always acquired (the dev stub returns
 * its fake token and the MSW worker ignores it). ETags round-trip verbatim.
 */

export interface AitaClientDeps {
  /** From useAuth().acquireTokenSilent — returns a bearer token string */
  acquireToken(): Promise<string>;
  /** config.apiBaseUrl — '' for same-origin (Vite proxy / SWA linked backend) */
  baseUrl: string;
}

export type ContentResult =
  | { status: 'ok'; content: AssessmentContent }
  | { status: 'not-found' }
  | { status: 'error'; httpStatus?: number };

export type InProgressResult =
  | { status: 'ok'; dto: InProgressAssessmentDto; etag: string | null }
  | { status: 'not-found' }
  | { status: 'error'; httpStatus?: number };

export type SaveResult =
  | { status: 'ok'; dto: InProgressAssessmentDto; etag: string | null }
  | { status: 'stale'; currentEtag: string | null }
  | { status: 'collision'; currentEtag: string | null }
  | { status: 'error'; httpStatus?: number };

export type SubmitResult =
  | { status: 'ok'; dto: SubmittedAssessmentDto }
  | { status: 'stale'; currentEtag: string | null }
  | { status: 'no-in-progress' }
  /**
   * fields: offending partner-details keys (camelCase content keys) — the missing keys for
   * partner_details_incomplete, the Other-selected key(s) for other_text_required /
   * other_text_ambiguous. Same { code, fields, detail } envelope as the save 400.
   */
  | { status: 'validation'; code?: string; fields?: string[]; detail?: string }
  | { status: 'error'; httpStatus?: number };

export type SubmittedResult =
  | { status: 'ok'; dto: SubmittedAssessmentDto }
  | { status: 'not-found' }
  | { status: 'error'; httpStatus?: number };

export type PdfResult =
  | { status: 'ok'; blob: Blob; filename: string }
  /** 503 — the API's typed transient miss (pdf_not_ready / pdf_storage_unavailable, Retry-After: 30). */
  | { status: 'not-ready'; httpStatus: number }
  | { status: 'unavailable'; httpStatus: number };

export type FacilitatorReportResult =
  | { status: 'ok'; report: FacilitatorReportDto }
  /** 403 — caller lacks the AitaFacilitator role (not in SG-AITA-Facilitators). */
  | { status: 'forbidden' }
  /** 503 — report reader (SQL) or the SharePoint overlay is not configured/reachable; transient. */
  | { status: 'unavailable'; httpStatus: 503 }
  | { status: 'error'; httpStatus?: number };

export interface AitaClient {
  getActiveContent(): Promise<ContentResult>;
  getContent(version: string): Promise<ContentResult>;
  getInProgress(): Promise<InProgressResult>;
  saveInProgress(body: SaveInProgressBody, etag?: string | null): Promise<SaveResult>;
  submit(idempotencyKey: string, etag: string): Promise<SubmitResult>;
  getLatest(): Promise<SubmittedResult>;
  getAssessment(id: string): Promise<SubmittedResult>;
  getPdf(id: string): Promise<PdfResult>;
  getFacilitatorReport(filters: FacilitatorReportFilters): Promise<FacilitatorReportResult>;
}

const API_ROOT = '/v1.3/api/aita';

/** A hung request must surface as a recoverable error, not an infinite spinner (WI 763388). */
const REQUEST_TIMEOUT_MS = 30_000;
/** PDF fetch gets longer headroom — a cold render behind the proxy can be slow. */
const PDF_TIMEOUT_MS = 60_000;

export function createAitaClient(deps: AitaClientDeps): AitaClient {
  async function request(
    path: string,
    init: {
      method?: string;
      headers?: Record<string, string>;
      body?: string;
      timeoutMs?: number;
    } = {},
  ): Promise<Response> {
    const token = await deps.acquireToken();
    // Timeout via Promise.race (not AbortSignal): the result is dropped after
    // the deadline and the caller maps the rejection to its recoverable error
    // state. Avoids AbortSignal realm coupling across browser/jsdom fetches.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () => reject(new Error('request-timeout')),
        init.timeoutMs ?? REQUEST_TIMEOUT_MS,
      );
    });
    try {
      return await Promise.race([
        fetch(`${deps.baseUrl}${API_ROOT}${path}`, {
          method: init.method ?? 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
            ...init.headers,
          },
          body: init.body,
        }),
        deadline,
      ]);
    } finally {
      clearTimeout(timer);
    }
  }

  async function getContentFrom(path: string): Promise<ContentResult> {
    try {
      const response = await request(path);
      if (response.status === 404) return { status: 'not-found' };
      if (!response.ok) return { status: 'error', httpStatus: response.status };
      return { status: 'ok', content: (await response.json()) as AssessmentContent };
    } catch {
      return { status: 'error' };
    }
  }

  async function getSubmittedFrom(path: string): Promise<SubmittedResult> {
    try {
      const response = await request(path);
      if (response.status === 404) return { status: 'not-found' };
      if (!response.ok) return { status: 'error', httpStatus: response.status };
      return { status: 'ok', dto: (await response.json()) as SubmittedAssessmentDto };
    } catch {
      return { status: 'error' };
    }
  }

  return {
    getActiveContent: () => getContentFrom('/content'),

    getContent: (version: string) => getContentFrom(`/content/${encodeURIComponent(version)}`),

    async getInProgress(): Promise<InProgressResult> {
      try {
        const response = await request('/assessment/in-progress');
        if (response.status === 404) return { status: 'not-found' };
        if (!response.ok) return { status: 'error', httpStatus: response.status };
        return {
          status: 'ok',
          dto: (await response.json()) as InProgressAssessmentDto,
          etag: response.headers.get('ETag'),
        };
      } catch {
        return { status: 'error' };
      }
    },

    async saveInProgress(body: SaveInProgressBody, etag?: string | null): Promise<SaveResult> {
      try {
        const response = await request('/assessment/in-progress', {
          method: 'PUT',
          headers: etag ? { 'If-Match': etag } : {},
          body: JSON.stringify(body),
        });
        if (response.status === 412) {
          return { status: 'stale', currentEtag: response.headers.get('ETag') };
        }
        if (response.status === 409) {
          return { status: 'collision', currentEtag: response.headers.get('ETag') };
        }
        if (!response.ok) return { status: 'error', httpStatus: response.status };
        return {
          status: 'ok',
          dto: (await response.json()) as InProgressAssessmentDto,
          etag: response.headers.get('ETag'),
        };
      } catch {
        return { status: 'error' };
      }
    },

    async submit(idempotencyKey: string, etag: string): Promise<SubmitResult> {
      try {
        const response = await request('/assessment/submit', {
          method: 'POST',
          headers: {
            'Idempotency-Key': idempotencyKey,
            'If-Match': etag,
          },
        });
        if (response.status === 412) {
          return { status: 'stale', currentEtag: response.headers.get('ETag') };
        }
        if (response.status === 404) return { status: 'no-in-progress' };
        if (response.status === 400) {
          let code: string | undefined;
          let fields: string[] | undefined;
          let detail: string | undefined;
          try {
            const body = (await response.json()) as {
              code?: string;
              fields?: string[];
              detail?: string;
            };
            code = body.code;
            // The controller serializes absent members as literal null — normalize
            // so the advertised `string[] | undefined` type holds downstream.
            fields = body.fields ?? undefined;
            detail = body.detail ?? undefined;
          } catch {
            // Non-JSON 400 body — report as plain validation failure.
          }
          return { status: 'validation', code, fields, detail };
        }
        if (!response.ok) return { status: 'error', httpStatus: response.status };
        return { status: 'ok', dto: (await response.json()) as SubmittedAssessmentDto };
      } catch {
        return { status: 'error' };
      }
    },

    getLatest: () => getSubmittedFrom('/assessment/latest'),

    getAssessment: (id: string) => getSubmittedFrom(`/assessment/${encodeURIComponent(id)}`),

    async getPdf(id: string): Promise<PdfResult> {
      try {
        const response = await request(`/assessments/${encodeURIComponent(id)}/pdf`, {
          timeoutMs: PDF_TIMEOUT_MS,
        });
        // 503 is the only transient signal; the body is deliberately NOT parsed so its
        // shape can never break this path (same resilience stance as before PR 49511).
        if (response.status === 503) return { status: 'not-ready', httpStatus: 503 };
        if (!response.ok) return { status: 'unavailable', httpStatus: response.status };
        return {
          status: 'ok',
          blob: await response.blob(),
          filename: `AITA-Report-${id}.pdf`,
        };
      } catch {
        return { status: 'unavailable', httpStatus: 0 };
      }
    },

    async getFacilitatorReport(
      filters: FacilitatorReportFilters,
    ): Promise<FacilitatorReportResult> {
      try {
        const query = new URLSearchParams();
        const filterEntries: [string, string[] | undefined][] = [
          ['organizationName', filters.organizationName],
          ['tenantId', filters.tenantId],
          ['department', filters.department],
          ['aiAdoptionStage', filters.aiAdoptionStage],
          ['overallBand', filters.overallBand],
          ['geographicRegion', filters.geographicRegion],
          ['tenantName', filters.tenantName],
          ['partnerName', filters.partnerName],
          ['mpnId', filters.mpnId],
          ['partnerOneId', filters.partnerOneId],
        ];
        filterEntries.forEach(([key, values]) => {
          values?.forEach((value) => query.append(key, value));
        });
        if (filters.submittedAtFrom) query.set('submittedAtFrom', filters.submittedAtFrom);
        if (filters.submittedAtTo) query.set('submittedAtTo', filters.submittedAtTo);
        const queryString = query.size > 0 ? `?${query.toString()}` : '';
        const response = await request(`/facilitator/report${queryString}`);
        if (response.status === 403) return { status: 'forbidden' };
        // 503 is the report reader / SharePoint overlay being unavailable — distinct from a
        // hard error so the page can show a "try again shortly" message instead of a dead end.
        if (response.status === 503) return { status: 'unavailable', httpStatus: 503 };
        if (!response.ok) return { status: 'error', httpStatus: response.status };
        return { status: 'ok', report: (await response.json()) as FacilitatorReportDto };
      } catch {
        return { status: 'error' };
      }
    },
  };
}
